/**
 * Location Detection Utility
 * Detects user location to restrict tax features to Kenya-based users only
 */

// Cache for location data to avoid repeated API calls
let locationCache = null;
let locationPromise = null;

/**
 * Detect user's country using multiple methods
 * @returns {Promise<string>} Country code (e.g., 'KE' for Kenya)
 */
export async function detectUserCountry() {
  // Return cached result if available
  if (locationCache) {
    return locationCache;
  }

  // Return existing promise if detection is in progress
  if (locationPromise) {
    return locationPromise;
  }

  locationPromise = performLocationDetection();
  return locationPromise;
}

/**
 * Perform the actual location detection
 */
async function performLocationDetection() {
  try {
    // Method 1: Check timezone for Kenya (Africa/Nairobi)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone === 'Africa/Nairobi') {
      locationCache = 'KE';
      return 'KE';
    }

    // Method 2: Use IP-based geolocation service (free tier)
    try {
      const response = await fetch('https://ipapi.co/json/', {
        timeout: 5000,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.country_code) {
          locationCache = data.country_code.toUpperCase();
          return locationCache;
        }
      }
    } catch (ipError) {
      console.warn('IP-based location detection failed:', ipError);
    }

    // Method 3: Fallback to browser language/locale hints
    const locale = navigator.language || navigator.userLanguage;
    if (locale && (locale.includes('sw') || locale.includes('KE'))) {
      locationCache = 'KE';
      return 'KE';
    }

    // Method 4: Check for Kenya-specific language preferences
    const languages = navigator.languages || [navigator.language];
    for (const lang of languages) {
      if (lang.includes('sw') || lang.includes('KE')) {
        locationCache = 'KE';
        return 'KE';
      }
    }

    // Default to unknown if all methods fail
    locationCache = 'UNKNOWN';
    return 'UNKNOWN';

  } catch (error) {
    console.warn('Location detection failed:', error);
    locationCache = 'UNKNOWN';
    return 'UNKNOWN';
  }
}

/**
 * Check if user is in Kenya
 * @returns {Promise<boolean>}
 */
export async function isUserInKenya() {
  const country = await detectUserCountry();
  return country === 'KE';
}

/**
 * Check if user should have access to tax features
 * @returns {Promise<boolean>}
 */
export async function canAccessKenyaTaxCalculator() {
  try {
    const isInKenya = await isUserInKenya();
    
    // Allow access if:
    // 1. User is in Kenya
    // 2. Location detection failed (give benefit of doubt)
    // 3. Development environment (for testing)
    const isDevelopment = process.env.NODE_ENV === 'development';
    const country = await detectUserCountry();
    
    return isInKenya || country === 'UNKNOWN' || isDevelopment;
  } catch (error) {
    console.warn('Access check failed:', error);
    // Allow access if check fails to avoid false negatives
    return true;
  }
}

/**
 * Get user-friendly location message
 * @returns {Promise<string>}
 */
export async function getLocationMessage() {
  const country = await detectUserCountry();
  
  if (country === 'KE') {
  return 'Welcome! Access to Invoices and proposals is available in your region.';
  }
  
  if (country === 'UNKNOWN') {
    return 'Unable to detect your location. Tax calculator access may be limited.';
  }
  
  return `Tax calculator is specifically designed for Kenya (KE). Detected location: ${country}. Consider using our global invoice and proposal tools instead.`;
}

/**
 * Reset location cache (useful for testing)
 */
export function resetLocationCache() {
  locationCache = null;
  locationPromise = null;
}

/**
 * Get alternative tools message for non-Kenya users
 */
export function getAlternativeToolsMessage() {
  return {
    title: 'Global Business Tools Available',
    message: 'While our tax calculator is designed specifically for Kenya, you can still use our powerful global tools:',
    tools: [
      {
        name: 'Invoice Generator',
        description: 'Create professional invoices with multi-currency support',
        link: '/send-invoice'
      },
      {
        name: 'Proposal Creator',
        description: 'Design compelling project proposals and quotes',
        link: '/project-pitch'
      },
      {
        name: 'Design Tool',
        description: 'Advanced drag-and-drop proposal designer',
        link: '/design-tool'
      }
    ]
  };
}
